import { zodResolver } from '@hookform/resolvers/zod'
import { Tags } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { useSaveCategory } from '../api'
import { categorySchema, type CategoryFormInput } from '../schemas'
import type { Category } from '../types'

interface CategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: Category
}

/** Mounted only while open, so the form starts fresh each time. */
export function CategoryDialog({ open, onOpenChange, category }: CategoryDialogProps) {
  const { t } = useTranslation('library')
  const save = useSaveCategory()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormInput>({
    resolver: zodResolver(categorySchema),
    defaultValues: { name: category?.name ?? '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await save.mutateAsync({ id: category?.id, name: values.name })
      toast.success(category ? t('Category renamed') : t('Category added'), values.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, ['name'])
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      icon={<Tags />}
      title={category ? t('Rename category') : t('New category')}
      dismissible={!isSubmitting}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="library-category-form" loading={isSubmitting}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="library-category-form" onSubmit={onSubmit} noValidate>
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input {...register('name')} autoFocus placeholder={t('e.g. Poetry')} />
        </FormField>
      </form>
    </Dialog>
  )
}
